import { Translation } from 'types/shared/helper';
import {
  SUPPORT_AI_UID,
  SUPPORT_AUTO_RESPONSE_UID,
  SUPPORT_SYSTEM_UID,
  SupportConversationStatus,
  SupportTicketStatus,
  SupportTicketType,
} from 'types/shared/documents/support';

/** Resolve a translated string for the active language, German-first. */
export const resolveTr = (
  language: string,
  value?: Translation<string>,
): string => {
  if (!value) return '';
  return value[language] ?? value.de ?? Object.values(value)[0] ?? '';
};

export const formatSupportTime = (ts?: number): string => {
  if (!ts) return '';
  return new Date(ts).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const conversationStatusLabel = (
  status: SupportConversationStatus,
): string =>
  // "resolved" and "blocked" are internal support states; to the restaurant
  // both still read as open, so they show the same label as "open".
  ({
    open: 'Offen',
    resolved: 'Offen',
    closed: 'Geschlossen',
    blocked: 'Offen',
  }[status] ?? status);

export const ticketStatusLabel = (status: SupportTicketStatus): string =>
  ({
    // "backlog" is our internal prioritisation state; to the restaurant the
    // ticket still reads as open.
    backlog: 'Offen',
    open: 'Offen',
    in_progress: 'In Bearbeitung',
    needs_review: 'In Prüfung',
    done: 'Erledigt',
    closed: 'Geschlossen',
  }[status] ?? status);

/** How far a ticket is, out of four steps. */
export const ticketStatusStep = (status: SupportTicketStatus): number =>
  ({
    backlog: 1,
    open: 1,
    in_progress: 2,
    needs_review: 3,
    done: 4,
    closed: 4,
  }[status] ?? 1);

export const ticketStatusHint = (status: SupportTicketStatus): string =>
  ({
    backlog: 'Wir haben dein Anliegen aufgenommen.',
    open: 'Wir haben dein Anliegen aufgenommen.',
    in_progress: 'Wir arbeiten gerade daran.',
    needs_review: 'Die Umsetzung ist fertig und wird gerade geprüft.',
    done: 'Ist umgesetzt.',
    closed: 'Dieses Ticket ist geschlossen.',
  }[status] ?? '');

export const ticketTypeLabel = (type: SupportTicketType): string =>
  ({
    feature: 'Funktionswunsch',
    bug: 'Fehler',
    request: 'Anfrage',
    billing: 'Abrechnung',
  }[type] ?? type);

export type SupportMessageRole = 'user' | 'ai' | 'support' | 'system';

/** Sender category of a message, from the current user's perspective. */
export const messageRole = (
  uid: string,
  selfIds: string[],
  fromCustomer?: boolean,
): SupportMessageRole => {
  if (uid === SUPPORT_AI_UID) return 'ai';
  if (uid === SUPPORT_AUTO_RESPONSE_UID || uid === SUPPORT_SYSTEM_UID) {
    return 'system';
  }
  // Own (restaurant) vs. Support: the API sets `fromCustomer` at write time
  // (true for the dashboard user, false for agents/AI). Fall back to uid
  // equality for messages written before the field existed.
  const own = fromCustomer ?? selfIds.includes(uid);
  return own ? 'user' : 'support';
};

export const roleLabel = (role: SupportMessageRole): string =>
  ({ user: 'Du', ai: 'KI', support: 'Support', system: 'System' }[role]);
